import { z } from 'zod';
import { AiModelSchema, AiReasoningEffortSchema, TranscriptionModelSchema } from '../../../../entities/organization/organization.schema';
import { AiFeatureKeySchema, AiFeatureOverrideSchema, AppModelOverrideKeySchema } from '../../../../shared/ai-features';
/**
 * Platform AI Settings Schema
 * Global AI model configuration managed by superadmin
 */ export const PlatformAiSettingsSchema = z.object({
    highModel: AiModelSchema,
    lowModel: AiModelSchema,
    transcriptionModel: TranscriptionModelSchema,
    // Per-feature override map. Values may be a specific model or a tier
    // (`'high' | 'low'`); tier values make the feature track the tier model.
    // Missing keys mean "use the feature's default tier" (resolved via AI_FEATURES).
    featureOverrides: z.partialRecord(AiFeatureKeySchema, AiFeatureOverrideSchema).default({}),
    // Same override semantics for installed apps, keyed by appId or appId:slot
    // (open string keys — apps register dynamically; the manifest's ai.text or
    // slot tier is the default when no override is set).
    appModelOverrides: z.record(z.string(), AiFeatureOverrideSchema).default({}),
    // Reasoning effort per tier, feature and app key. A missing value inherits
    // the effort of wherever the model comes from (resolveTierModel); a tier
    // without one runs its model's default.
    highReasoningEffort: AiReasoningEffortSchema.optional(),
    lowReasoningEffort: AiReasoningEffortSchema.optional(),
    featureReasoningEfforts: z.partialRecord(AiFeatureKeySchema, AiReasoningEffortSchema).default({}),
    appReasoningEfforts: z.record(z.string(), AiReasoningEffortSchema).default({})
});
/**
 * Show Platform AI Settings Response Schema
 * GET /api/v2/platform/ai-settings
 */ export const ShowPlatformAiSettingsResponseSchema = z.object({
    data: PlatformAiSettingsSchema
});
/**
 * Update Platform AI Settings Request Schema
 * PATCH /api/v2/platform/ai-settings
 *
 * `featureOverrides` accepts `null` per key to clear a previously-set override
 * (the server translates `null` into `$unset` on that map entry). The
 * reasoning-effort fields clear the same way.
 */ export const UpdatePlatformAiSettingsRequestSchema = z.object({
    highModel: AiModelSchema.optional(),
    lowModel: AiModelSchema.optional(),
    transcriptionModel: TranscriptionModelSchema.optional(),
    featureOverrides: z.partialRecord(AiFeatureKeySchema, AiFeatureOverrideSchema.nullable()).optional(),
    appModelOverrides: z.record(AppModelOverrideKeySchema, AiFeatureOverrideSchema.nullable()).optional(),
    highReasoningEffort: AiReasoningEffortSchema.nullable().optional(),
    lowReasoningEffort: AiReasoningEffortSchema.nullable().optional(),
    featureReasoningEfforts: z.partialRecord(AiFeatureKeySchema, AiReasoningEffortSchema.nullable()).optional(),
    appReasoningEfforts: z.record(AppModelOverrideKeySchema, AiReasoningEffortSchema.nullable()).optional()
}).refine((data)=>Object.values(data).some((v)=>v !== undefined), {
    message: 'At least one field must be provided'
});
/**
 * Update Platform AI Settings Response Schema
 */ export const UpdatePlatformAiSettingsResponseSchema = z.object({
    data: PlatformAiSettingsSchema
});

//# sourceMappingURL=platform-ai-settings.schema.js.map